'use client';

import { useEffect, useRef } from 'react';
import { isProductionEnv } from '~/lib/env';

const VITEC_ANALYTICS_BASE_URL = 'https://connect-analytics.maklare.vitec.net';
const VITEC_ANALYTICS_SCRIPT_PATH = '/scripts/v1/analytics.min.js';

declare global {
  interface Window {
    VitecMAnalytics?: Record<string, unknown>;
  }
}

/** Type-safe wrapper around the global `va` function injected by Vitec Analytics. */
function vitecVa(...args: unknown[]) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const fn = (window as any).va;
  if (typeof fn === 'function') fn(...args);
}

interface VitecAnalyticsOptions {
  /** Vitec Analytics API key */
  apiKey: string | undefined;
  /** The Vitec estate/provider ID (CURRENT_ESTATE_ID) */
  estateId: string | undefined;
  /** The Vitec customer ID for the current estate (e.g. "S12712") */
  customerId: string | null | undefined;
}

/**
 * Loads the Vitec Analytics script and initialises tracking for a specific estate.
 *
 * The script is loaded once per page; subsequent calls with different
 * estateId re-issue the `va()` commands without reloading the script.
 */
export function useVitecAnalytics({
  apiKey,
  estateId,
  customerId,
}: VitecAnalyticsOptions) {
  const scriptLoaded = useRef(false);

  useEffect(() => {
    console.debug('[VitecAnalytics] Hook called', {
      apiKey: !!apiKey,
      estateId,
      customerId,
    });

    if (
      typeof window === 'undefined' ||
      !apiKey ||
      !estateId ||
      !customerId ||
      !isProductionEnv()
    ) {
      console.debug(
        '[VitecAnalytics] Skipping (non-production or missing params)'
      );
      return;
    }

    // Bootstrap the global queue exactly once
    if (!scriptLoaded.current) {
      console.debug('[VitecAnalytics] Initializing script...');
      window.VitecMAnalytics = window.VitecMAnalytics || {};
      window.VitecMAnalytics.u = VITEC_ANALYTICS_BASE_URL;

      // Create the lightweight queue function that Vitec's script expects as `window.va`
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).va = function () {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const q = ((window.VitecMAnalytics as any).q =
          (window.VitecMAnalytics as any).q || []);
        // eslint-disable-next-line prefer-rest-params
        q.push(arguments);
      };

      // Inject the script tag
      const script = document.createElement('script');
      script.src = `${VITEC_ANALYTICS_BASE_URL}${VITEC_ANALYTICS_SCRIPT_PATH}`;
      script.async = true;
      script.onload = () =>
        console.debug('[VitecAnalytics] Script loaded successfully');
      script.onerror = (e) =>
        console.error('[VitecAnalytics] Script failed to load', e);
      const firstScript = document.getElementsByTagName('script')[0];
      firstScript?.parentNode?.insertBefore(script, firstScript);

      scriptLoaded.current = true;
      console.debug('[VitecAnalytics] Script tag injected');
    }

    // Issue tracking commands (safe to call multiple times – the queue handles it)
    console.debug('[VitecAnalytics] Sending commands', {
      apiKey: apiKey.slice(0, 4) + '...',
      estateId,
      customerId,
    });
    vitecVa('create', apiKey);
    vitecVa('set', 'target', estateId);
    vitecVa('set', 'target_customer_id', customerId);
  }, [apiKey, estateId, customerId]);
}
